/**
 * VoteBurst - flying vote emojis for Gender Party
 * When someone votes BOY → 👶 floats up on the left with blue glow
 * When someone votes GIRL → 👶 floats up on the right with pink glow
 * Driven by vote_update deltas — no user interaction required.
 */

import { useState, useEffect, useRef, memo } from "react";
import type { GenderVote } from "../hooks";
import styles from "./VoteBurst.module.css";

type AnimationType = "FloatLeft" | "FloatRight" | "FloatCenter";

interface FloatingVote {
  id: string;
  vote: GenderVote;
  x: number;
  delay: number;
  scale: number;
  animationType: AnimationType;
}

interface VoteBurstProps {
  /** New boy votes delta (how many new boy votes arrived) */
  boyDelta: number;
  /** New girl votes delta */
  girlDelta: number;
  /** Trigger counter — increment to spawn a burst */
  trigger: number;
  /** Size variant for broadcast vs mobile */
  size?: "small" | "medium" | "large";
}

let voteIdCounter = 0;

const BOY_EMOJI = "👶";
const GIRL_EMOJI = "👶";

export const VoteBurst = memo(function VoteBurst({
  boyDelta,
  girlDelta,
  trigger,
  size = "medium",
}: VoteBurstProps) {
  const [floatingVotes, setFloatingVotes] = useState<FloatingVote[]>([]);
  const prevTriggerRef = useRef(trigger);

  useEffect(() => {
    if (trigger === prevTriggerRef.current) return;
    prevTriggerRef.current = trigger;

    if (boyDelta <= 0 && girlDelta <= 0) return;

    const newVotes: FloatingVote[] = [];
    const animationTypes: AnimationType[] = ["FloatLeft", "FloatRight", "FloatCenter"];

    // Boy votes — left side (5–40%)
    const clampedBoy = Math.min(boyDelta, 6);
    for (let i = 0; i < clampedBoy; i++) {
      newVotes.push({
        id: `vote-${++voteIdCounter}`,
        vote: "BOY",
        x: 5 + Math.random() * 35,
        delay: Math.random() * 500,
        scale: 0.8 + Math.random() * 0.5,
        animationType: animationTypes[Math.floor(Math.random() * animationTypes.length)],
      });
    }

    // Girl votes — right side (60–95%)
    const clampedGirl = Math.min(girlDelta, 6);
    for (let i = 0; i < clampedGirl; i++) {
      newVotes.push({
        id: `vote-${++voteIdCounter}`,
        vote: "GIRL",
        x: 60 + Math.random() * 35,
        delay: Math.random() * 500,
        scale: 0.8 + Math.random() * 0.5,
        animationType: animationTypes[Math.floor(Math.random() * animationTypes.length)],
      });
    }

    setFloatingVotes((prev) => [...prev, ...newVotes]);

    // Clean up after animation
    setTimeout(() => {
      setFloatingVotes((prev) => prev.filter((v) => !newVotes.find((nv) => nv.id === v.id)));
    }, 6500);
  }, [trigger, boyDelta, girlDelta]);

  useEffect(() => {
    return () => setFloatingVotes([]);
  }, []);

  const sizeClass = size === "large" ? styles.large : size === "small" ? styles.small : "";

  return (
    <div className={`${styles.container} ${sizeClass}`}>
      {floatingVotes.map((v) => (
        <div
          key={v.id}
          className={`${styles.vote} ${v.vote === "BOY" ? styles.boy : styles.girl}`}
          style={{
            left: `${v.x}%`,
            animationDelay: `${v.delay}ms`,
            "--scale": v.scale,
            animationName:
              v.animationType === "FloatLeft"
                ? styles.reactionFloatLeft
                : v.animationType === "FloatRight"
                  ? styles.reactionFloatRight
                  : styles.reactionFloatCenter,
          } as React.CSSProperties}
        >
          {v.vote === "BOY" ? BOY_EMOJI : GIRL_EMOJI}
        </div>
      ))}
    </div>
  );
});
